export const LOUNGE_POST_TIMEOUT_MS = 15000;
export const LOUNGE_GET_TIMEOUT_MS = 15000;

const inFlightPosts = new Map<string, Promise<unknown>>();
const abortTimeoutClears = new WeakMap<AbortSignal, () => void>();

export function abortSignalWithTimeout(ms: number): AbortSignal {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  const clearTimer = () => clearTimeout(timer);
  controller.signal.addEventListener('abort', clearTimer, { once: true });
  abortTimeoutClears.set(controller.signal, clearTimer);
  return controller.signal;
}

export function clearAbortTimeout(signal: AbortSignal) {
  const clearTimer = abortTimeoutClears.get(signal);
  if (!clearTimer) {
    return;
  }

  clearTimer();
  abortTimeoutClears.delete(signal);
}

export function loungeFetchRejectValue(error: unknown): { message: string } {
  const err = error as { name?: string; message?: string } | undefined;
  if (err?.name === 'AbortError' || err?.name === 'TimeoutError') {
    return { message: 'Request timed out' };
  }

  return { message: err?.message || 'Request failed' };
}

export function loungeApiRejectValue(payload: unknown): { message: string } {
  if (typeof payload === 'string' && payload) {
    return { message: payload };
  }

  if (payload && typeof payload === 'object') {
    const rec = payload as { message?: unknown; error?: unknown };
    if (typeof rec.message === 'string' && rec.message) {
      return { message: rec.message };
    }
    if (typeof rec.error === 'string' && rec.error) {
      return { message: rec.error };
    }
    if (rec.error && typeof rec.error === 'object') {
      const nested = rec.error as { message?: unknown };
      if (typeof nested.message === 'string' && nested.message) {
        return { message: nested.message };
      }
    }
  }

  return { message: 'Request failed' };
}

export function buildLoungePostHeaders(token: string | null): Record<string, string> {
  return {
    Authorization: `bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

export function loungeReplyLockKey(
  park: 'dl' | 'wdw',
  chatId: unknown,
  chatReplyId: unknown,
  parentReplyId: unknown
): string {
  return `reply:${park}:${chatId}:${chatReplyId}:${parentReplyId ?? 'root'}`;
}

export function runExclusiveLoungePost<T>(
  key: string,
  run: () => Promise<T>
): Promise<T> {
  const existing = inFlightPosts.get(key);
  if (existing) {
    return existing as Promise<T>;
  }

  const promise = run().finally(() => {
    if (inFlightPosts.get(key) === promise) {
      inFlightPosts.delete(key);
    }
  });
  inFlightPosts.set(key, promise);
  return promise;
}

export function resetExclusiveLoungePostsForTests() {
  if (process.env.NODE_ENV === 'test') {
    inFlightPosts.clear();
  }
}
